/**
 * CatalogRow — one catalogue entry as a single list row (brief-26).
 *
 * Density over presentation: mark, title, one-line truncated description, badge,
 * and the same admin actions the card carries. Everything is on ONE line — the
 * description truncates with an ellipsis rather than wrapping, so ten services
 * scan at a glance and every row has identical height.
 *
 * The card's rules carry over unchanged: no commerce CTA, no detail modal and no
 * dimming in the admin catalogue (convention-admin-catalogue-no-commerce-cta),
 * and the two-stage hover — silhouette on row hover, logo orange on the control
 * the pointer is on.
 */

import { useState } from 'react';
import { t } from '../i18n';
import type { TileConfig } from '../config/schema';
import type { Route } from '../shell/Router';
import PinToggle from './PinToggle';
import DeployRolloutModal from './DeployRolloutModal';
import EditDescriptionModal from './EditDescriptionModal';

interface CatalogRowProps {
  tile: TileConfig;
  badgeClass: string;
  badgeLabel: string;
  pinned: boolean;
  onTogglePin: () => void;
  onNavigate?: (path: Route) => void;
}

export default function CatalogRow({
  tile, badgeClass, badgeLabel, pinned, onTogglePin, onNavigate,
}: CatalogRowProps) {
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  return (
    <>
      <li className={`dp-row${pinned ? ' dp-row--pinned' : ''}`}>
        <span className="dp-row__pin">
          <PinToggle pinned={pinned} title={tile.title} onToggle={onTogglePin} />
        </span>

        <span className="dp-row__mark" aria-hidden="true">{tile.mark}</span>

        <span className="dp-row__text">
          <span className="dp-row__title">{tile.title}</span>
          <span className="dp-row__desc">{tile.description}</span>
        </span>

        <span className={`dp-badge dp-badge--inline ${badgeClass}`}>{badgeLabel}</span>

        <span className="dp-row__actions">
          <button
            type="button"
            className="dp-btn dp-btn--sm dp-btn--ghost"
            onClick={() => onNavigate?.(`/apps/${tile.id}/operations`)}
          >
            {t('tile.admin')}
          </button>
          <button
            type="button"
            className="dp-btn dp-btn--sm dp-btn--ghost"
            onClick={() => setShowDeployModal(true)}
          >
            {t('tile.deploy.action')}
          </button>
          <button
            type="button"
            className="dp-btn dp-btn--sm dp-btn--ghost"
            onClick={() => setShowEditModal(true)}
          >
            {t('tile.editDescription.action')}
          </button>
        </span>
      </li>

      {showDeployModal && (
        <DeployRolloutModal tile={tile} onClose={() => setShowDeployModal(false)} />
      )}
      {showEditModal && (
        <EditDescriptionModal tile={tile} onClose={() => setShowEditModal(false)} />
      )}
    </>
  );
}
